import React, { useState } from 'react'

import { StyleSheet, Keyboard, Platform, ScrollView, View, Image, TouchableOpacity } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import client from '@sart/client'
import { useContent } from '@sart/content'
import { prettyErrorMessage, testID } from '@sart/utilities'
import { adaptive, backgroundColors, typography, colors } from '@sart/styles'
import AppConfig from '../Configuration'
import CameraComponent from './CameraComponent'


import {
  Text,
  Title,
  Description,
  Space,
  Button,
  TextInputExtended,
  KeyboardAvoidingView,
  ReturnFooter,
  Anchor
} from '@sart/components'

import NeedHelpFooter from '../NeedHelpFooter'
import * as theme from './signTheme'
import { Camera, CompanyIcon } from '@sart/icons'
import { Dimensions } from 'react-native'
import { min } from 'lodash'
import { number } from 'currency-codes'

export default function SignUpRegisterICCID(props) {
  const content = useContent()

  const { navigation, route } = props
  const email = route.params?.email

  const [scanning, setScanning] = useState(false)

  let emailRef

  const formik = useFormik({
    initialValues: {
      number: '',
      email: ''
    },
    validationSchema: Yup.object().shape({
      number: Yup.string().trim().required(content.forms.required)
    }),
    onSubmit: handlePhoneRegistration,
  })

  function barCodeProcessed(data) {
    setScanning(false)
    if (data != undefined) {
      formik.setFieldValue('number', data.value.substring(data.value.length - 5, data.value.length))
      formik.setFieldTouched('number', true, false)
    }
  }

  async function linkPsimToCustomer(email, iccid) {
    await client.signInWithAgentUser({
      username: AppConfig.guestUsername,
      password: AppConfig.guestPassword
    })

    const catalogs = await client.callApi('catalog', 'getCatalogs', [{ operatorCode: AppConfig.catalogName }])
    const catalog = catalogs[0]
    const catalogId = catalog.catalogId

    console.log('Linking user with email ' + email + ' and ICCID ' + iccid);

    // link customer to iccid
    const linkAccountProducts = await client.callApi('catalog', 'getLinkAccountProducts', [catalogId])
    console.log('Link account products: ' + JSON.stringify(linkAccountProducts, null, 2))
    const linkProduct = linkAccountProducts.find(product => product.productLegacyId === AppConfig.linkPsimProduct)

    if (!linkProduct) {
      throw new Error("Link account product not found");
    }

    const extendedData = {
      email,
      iccid,
    }

    //sumbit order, we actually need to submit this as a guest but then poll on the order as an agent
    let basket, order
    try {
      basket = await client.createBasket()
      basket = await client.addCustomerProductToBasket(basket, linkProduct, extendedData)
      basket = await client.confirmBasket(basket)

      order = await client.processConfirmedBasketNoPayment(basket)

      //sign in as agent to poll order status
      await client.signInWithAgentUser({
        username: AppConfig.agentUsername,
        password: AppConfig.agentPassword
      })


      order = await client.pollOrderForStatus(client.cache.customerId, order.orderId)

      client.unsetCache('currentBasketId')
      client.unsetCache('basket')

      if (order && order.status === 'failed') {
        console.log('linking order failed with message: ' + JSON.stringify(order, null, 2))
        const orderError = new Error(order.message)
        orderError.response = { body: { code: order.message } }
        throw orderError
      }

    } catch (error) {
      console.log('Error linking psim to customer: ' + JSON.stringify(error))
      throw error
    }

    console.log('Order placed: ' + JSON.stringify(order, null, 2))
  }

  async function handlePhoneRegistration(values, form) {
    Keyboard.dismiss()
    form.setSubmitting(true)
    try {
      if (values.number.length === 5) {
        values.number = AppConfig.iccidPrefix + values.number.trim()
      }

      await linkPsimToCustomer(email, values.number);

      await client.registration.registerUser({
        username: email,
        primaryIdentifier: values.number,
        operatorCode: AppConfig.operatorCode,
        //TODO This should be captured at the front end. Especially if there are different codes for different regions.
        twoFactorAuthNumber: '07506736598',
        twoFactorAuthNumberCountryCode: "+44",
        emailAddress: email
      })

      const auth = await client.signInWithVerificationCode({ email: email, code: '222222' })
      const user = await client.callApi('user', 'getUser', [auth.userId])

      user.regCompleted = true
      user.regCompletedDate = null
      await client.callApi('user', 'modifyUser', [user.userId, user])

      const newUser = await client.callApi('user', 'getUser', [user.userId])

      console.log('AFTER REGISTRATION USER IS ' + JSON.stringify(newUser, null, 2));

      const params = { ...auth, email: email, number: values.number, ncaFlow: false }

      navigation.navigate('SetPassword', params)


    } catch (error) {
      //undo the number change from before
      // values.number = values.number.replace(AppConfig.iccidPrefix, '')
      console.log('Error during registration:', JSON.stringify(error))
      form.setSubmitting(false)
      const message = prettyErrorMessage(error, 'registration', content)
      form.setFieldError('number', message)
    }
  }

  return (
    <View style={styles.container}>
      {!scanning &&
        // <ScrollView style={styles.scrollViewContainer}>
        <KeyboardAvoidingView style={styles.content}>
          <Space height={styles.spaceHeightOne.height} />
          <View style={styles.logo}>
            <Image
              source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
              //height={styles.logoStyle.height} width={200} alignSelf={"center"}
              style={styles.logoImage}
            />
            {/* <CompanyIcon color={colors.white} height={styles.logoStyle.height} width={200} /> */}
          </View>
          <Title {...theme.title}>{content.register_number.title}</Title>

          <Space height={styles.spaceHeightTwo.height} />
          <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
            <View style={{ flex: 1 }}>
              <TextInputExtended
                label={content.register_number.iccidLabel}
                value={formik.values.number}
                placeholder={content.register_number.iccidPlaceholder}
                keyboardType="phone-pad"
                onChangeText={formik.handleChange('number')}
                onBlur={formik.handleBlur('number')}
                error={formik.touched.number && formik.errors.number}
                style={styles.textInput}
                labelStyle={styles.labelStyle}
                onSubmitEditing={formik.handleSubmit}
                footerErrorContainerStyle={styles.errorContainer}
                {...testID('SignUpRegisterNumber')}
              />
            </View>
            <TouchableOpacity
              style={styles.camera}
              onPress={() => setScanning(true)}
            >
              <Camera onPress={() => setScanning(true)} color={colors.brightOrange} />
            </TouchableOpacity>
          </View>

          <Space height={24} />

          <Button
            title={
              formik.isSubmitting
                ? content.register_number.inProgress
                : content.register_number.action
            }
            disabled={!formik.isValid || formik.isSubmitting}
            onPress={formik.handleSubmit}
            {...theme.button}
            titleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white }}
            disabledTitleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white + 45 }}
            {...testID('SignUpRegisterSubmit')}
          />
          <Space height={12} />

          {/* <Text style={styles.footerStyle}>{content.register_number.note}</Text> */}

          {/* <Space height={24} /> */}
          <ReturnFooter
            cancelStyle={styles.cancelStyle}
            title={content.common.cancel}
            onPress={() => navigation.goBack()}
          />
        </KeyboardAvoidingView>
        // </ScrollView>
      }
      {scanning &&
        <CameraComponent
          barCodeProcessed={barCodeProcessed}
        />
      }
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
    minHeight: '100%',
    // height: Dimensions.get('window').height
    //alignItems: 'center'
    ...Platform.select({
      web: {
        alignItems: 'center'
      }
    })
  },
  scrollViewContainer: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
  },
  content: {
    flex: 1,
    ...adaptive.wrapperPadding,
    ...adaptive.logoPadding,
    backgroundColor: backgroundColors.darkCharcoal,
    // height: '100%',
    // paddingTop: 24,
    // ...adaptive.contentHeight,
    // height: Dimensions.get('window').height
    maxWidth: 600,
    width: '100%'
  },
  cancel: {
    justifyContent: 'center',
    alignItems: 'center',
    // padding: 24
  },
  logoStyle: {
    ...adaptive.guruSize
  },
  cancelStyle: {
    color: colors.white
  },
  footerStyle: {
    color: colors.white
  },
  spaceHeightOne: {
    ...adaptive.spaceHeightRegisterScreenOne
  },
  spaceHeightTwo: {
    ...adaptive.spaceHeightRegisterScreenTwo
  },
  spaceHeightThree: {
    ...adaptive.spaceHeightRegisterScreenThree
  },
  description: {
    textAlign: 'center',
    ...typography.demi,
    color: colors.srGreen
  },
  link: {
    color: colors.clear,
    fontSize: 14
  },
  logo: {
    alignSelf: "center"
  },
  text: {
    color: colors.srGreen,
    fontSize: 14
  },
  camera: {
    // position: 'absolute',
    // height: '100%',
    justifyContent: 'center',
    paddingLeft: '3%',
    paddingTop: '10%',
    maxWidth: 40,
    ...Platform.select({
      web: {
        marginTop: 'auto',
      },
      default: {
        marginTop: 8,
      }
    })
    // alignSelf: 'flex-end',
  },
  textInput: {
    color: colors.srTextInput,
    backgroundColor: backgroundColors.white,
    height: 48,
    borderRadius: 16,
    minWidth: 300,
    width: '100%'
  },
  labelStyle: {
    color: colors.brightOrange,
    ...typography.demi
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative',
    alignSelf: 'center',
    width: 200,
    ...adaptive.guruSize
  },
  errorContainer: {
    maxWidth: 320,
  }
})
